import React, { useEffect, useState } from "react";
import { Card, Row, Col, Progress, Typography, Tag, Button } from "antd";
import { ExclamationCircleOutlined, RiseOutlined, WarningOutlined } from "@ant-design/icons";
import { PieChart, ResponsiveContainer, Pie, Cell } from "recharts";
import { HUMAN_RISK_SCORECARD, POPULATE_USER_RISK_SCORE } from "../../apis/apis";
import CulsightPageLoader from "../../components/CulsightPageLoader";

const { Title, Text } = Typography;

const HumanRiskScoreCard = () => {

    const [apiData, setApiData] = useState(null);
    const [loading, setLoading] = useState(true);

    const COLOR_MAP = {
        excellent: "#13c2c2",
        good: "#22c55e",
        fair: "#f59e0b",
        poor: "#f97316",
        critical: "#ef4444"
    };

    // ✅ Grade → category mapping
    const GRADE_COLOR_MAP = {
        A: "excellent",
        B: "good",
        C: "fair",
        D: "poor",
        F: "critical"
    };

    const getUserRiskKey = (value) => {
        if (value >= 75) return "critical";
        if (value >= 50) return "poor";
        if (value >= 30) return "fair";
        if (value >= 15) return "good";
        return "excellent";
    };

    // ✅ Fetch API
    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await HUMAN_RISK_SCORECARD();
            if (res?.data?.status) {
                setApiData(res.data.data);
            }
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const POPULATE_USER_RISK_SCORE_BUTTON = async () => {
        setLoading(true);
        try {
            const res = await POPULATE_USER_RISK_SCORE();
            if (res?.data?.status) {

            }
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    // ✅ Safe defaults
    const score = apiData?.organization_score ?? 0;
    const maxScore = 1000;
    const percent = Math.round((score / maxScore) * 100);

    const grade = apiData?.grade || "-";
    const gradeStatus = apiData?.grade_status || "-";

    // ✅ Dynamic grade color
    const gradeKey = GRADE_COLOR_MAP[grade] || "fair";
    const gradeColor = COLOR_MAP[gradeKey];

    // ✅ Donut Data
    const data = [
        { name: "Excellent", key: "excellent", value: apiData?.donut_chart?.excellent ?? 0 },
        { name: "Good", key: "good", value: apiData?.donut_chart?.good ?? 0 },
        { name: "Fair", key: "fair", value: apiData?.donut_chart?.fair ?? 0 },
        { name: "Poor", key: "poor", value: apiData?.donut_chart?.poor ?? 0 },
        { name: "Critical", key: "critical", value: apiData?.donut_chart?.critical ?? 0 }
    ];

    const distribution = [
        { label: "Excellent (750-1000)", key: "excellent", value: apiData?.donut_chart?.excellent ?? 0 },
        { label: "Good (650-749)", key: "good", value: apiData?.donut_chart?.good ?? 0 },
        { label: "Fair (500-649)", key: "fair", value: apiData?.donut_chart?.fair ?? 0 },
        { label: "Poor (350-499)", key: "poor", value: apiData?.donut_chart?.poor ?? 0 },
        { label: "Critical (<350)", key: "critical", value: apiData?.donut_chart?.critical ?? 0 }
    ];


    // ✅ Users safe mapping
    const users =
        apiData?.highest_risk_users?.map((user) => ({
            name: user.name,
            email: user.email,
            department: user.department,
            susceptibility: user.susceptibility
        })) || [];

    // ✅ Loading State
    if (loading) {
        return (
            <div style={{ textAlign: "center", marginTop: 100 }}>
                <CulsightPageLoader />
            </div>
        );
    }

    return (
        <div className="lms-body">


            <Row align="middle" style={{ marginBottom: 20 }}>
                <Col span={12}>
                    <h1>Human Risk Scorecard</h1>
                    <span style={{ color: "gray", fontSize: "16px", fontWeight: "600" }}>
                        Organization-wide security awareness score
                    </span>
                </Col>
                <Col span={12}>
                    <Button type="primary" style={{ float: "right" }} onClick={POPULATE_USER_RISK_SCORE_BUTTON}>
                        Refresh Risk Data
                    </Button>
                </Col>
            </Row>

            <Row gutter={24} style={{ marginTop: 20 }}>
                <Col xs={24} md={10}>
                    <Card style={{ borderRadius: 16}}>
                        <div style={{ textAlign: "center" }}>
                            <Progress
                                type="circle"
                                percent={percent}
                                strokeColor={gradeColor}
                                trailColor="#e5e7eb"
                                strokeWidth={8}
                                width={180}
                                format={() => (
                                    <div>
                                        <div style={{ fontSize: 50, fontWeight: 900, color: gradeColor }}>
                                            {score}
                                        </div>
                                        <div style={{ color: "#6b7280", fontSize: 18 }}>
                                            out of {maxScore}
                                        </div>
                                    </div>
                                )}
                            />

                            <div style={{ marginTop: 20 }}>
                                <Tag
                                    style={{
                                        padding: "10px 18px",
                                        fontSize: 22,
                                        fontWeight: 900,
                                        color: gradeColor,
                                        backgroundColor: `${gradeColor}20`,
                                        border: "none",
                                        borderRadius: 10
                                    }}
                                >
                                    {grade}
                                </Tag>

                                <div style={{ marginTop: 10, color: gradeColor, fontWeight: 700 }}>
                                    {gradeStatus}
                                </div>
                            </div>
                        </div>
                    </Card>
                </Col>

                <Col xs={24} md={14}>
                    <Card style={{ borderRadius: 16,height:"100%" }}>
                        <Title level={4}>Score Distribution</Title>

                        <Row align="middle">
                            <Col span={12}>
                                <ResponsiveContainer width="100%" height={220}>
                                    <PieChart>
                                        <Pie
                                            data={data}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={60}
                                            outerRadius={90}
                                            paddingAngle={3}
                                            dataKey="value"
                                            startAngle={90}
                                            endAngle={-270}
                                        >
                                            {data.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLOR_MAP[entry.key]} />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                            </Col>

                            <Col span={12}>
                                {distribution.map((item, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            marginBottom: 12
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            <div
                                                style={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: "50%",
                                                    background: COLOR_MAP[item.key]
                                                }}
                                            />
                                            <Text>{item.label}</Text>
                                        </div>
                                        <Text strong>{item.value}</Text>
                                    </div>
                                ))}
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>


            <Row gutter={[20, 20]} style={{ marginTop: "20px" }}>

                <Col xs={24} sm={12} md={12} lg={8}>
                    <Card
                        hoverable
                        style={{
                            borderRadius: 16,
                            height: 80,
                            display: "flex",
                            alignItems: "center",
                        }}
                        bodyStyle={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                        }}

                    >
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <div
                                style={{
                                    background: "#ede9fe",
                                    padding: "10px",
                                    borderRadius: "12px",
                                    marginLeft: "-180px"

                                }}
                            >
                                <WarningOutlined style={{ color: "#8c3add", fontSize: 18 }} />
                            </div>

                            <div>
                                <h2 style={{ margin: 0 }}>{apiData?.total_users}</h2>
                                <span style={{ color: "gray", fontSize: "13px" }}>
                                    Total Assessed
                                </span>
                            </div>
                        </div>


                    </Card>
                </Col>

                <Col xs={24} sm={12} md={12} lg={8}>
                    <Card
                        hoverable
                        style={{
                            borderRadius: 16,
                            height: 80,
                            display: "flex",
                            alignItems: "center",
                        }}
                        bodyStyle={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",

                        }}

                    >
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <div
                                style={{
                                    background: "#d1fae5",
                                    padding: "10px",
                                    borderRadius: "12px",
                                    marginLeft: "-180px"

                                }}
                            >
                                <ExclamationCircleOutlined style={{ color: "#1fa7a1", fontSize: 18 }} />
                            </div>

                            <div>
                                <h2 style={{ margin: 0 }}>{apiData?.excellent_users}</h2>
                                <span style={{ color: "gray", fontSize: "13px" }}>
                                    Excellent Scores             </span>
                            </div>
                        </div>


                    </Card>
                </Col>

                {/* Insider Risk */}
                <Col xs={24} sm={12} md={12} lg={8}>
                    <Card
                        hoverable
                        style={{
                            borderRadius: 16,
                            height: 80,
                            display: "flex",
                            alignItems: "center",
                        }}
                        bodyStyle={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                        }}

                    >
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <div
                                style={{
                                    background: "#fee2e2",
                                    padding: "10px",
                                    borderRadius: "12px",
                                    marginLeft: "-180px"

                                }}
                            >
                                <RiseOutlined style={{ color: "red", fontSize: 18 }} />
                            </div>

                            <div>
                                <h2 style={{ margin: 0 }}>{apiData?.needs_training_users}</h2>
                                <span style={{ color: "gray", fontSize: "13px" }}>
                                    Needs Training
                                </span>
                            </div>
                        </div>


                    </Card>
                </Col>


            </Row>


            <Card style={{ marginTop: "10px" }}>
                <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <WarningOutlined style={{ color: "red" }} />
                    Highest Susceptibility Users
                </h2>

                {users.map(({ name, email, department, susceptibility }) => {
                    const riskKey = getUserRiskKey(susceptibility);
                    const userColor = COLOR_MAP[riskKey]; // ✅ defined here
                    // Get initials for avatar circle
                    const initials = name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .toUpperCase();

                    return (
                        <div
                            key={email}
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                backgroundColor: "#f9f9f9",
                                padding: "15px",
                                marginTop: 16,
                                borderRadius: 8,
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                                <div
                                    style={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: "50%",
                                        backgroundColor: `${userColor}20`,
                                        color: userColor,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontWeight: "bold",
                                        fontSize: 18,
                                        userSelect: "none",
                                    }}
                                >
                                    {initials}
                                </div>
                                <div>
                                    <strong style={{ fontSize: 16 }}>{name}</strong>
                                    <div style={{ fontSize: 14, color: "#666" }}>{email}</div>
                                    <div
                                        style={{
                                            marginTop: 6,
                                            display: "inline-block",
                                            backgroundColor: "#eee",
                                            color: "#444",
                                            padding: "2px 8px",
                                            borderRadius: 12,
                                            fontSize: 12,
                                            fontWeight: "600",
                                            userSelect: "none",
                                        }}
                                    >
                                        {department}
                                    </div>
                                </div>
                            </div>
                            <div style={{ textAlign: "right", minWidth: 70 }}>
                                <div
                                    style={{
                                        fontWeight: "bold",
                                        fontSize: 20,
                                        color: userColor,
                                        userSelect: "none",
                                    }}
                                >
                                    {susceptibility}%
                                </div>
                                <div style={{ fontSize: 12, color: "#888" }}>Susceptibility</div>
                            </div>
                        </div>
                    );
                })}
            </Card>

            <div style={{marginTop:"15px",backgroundColor:"white",padding:"20px",borderRadius:"12px"}}>
            <span style={{color:"gray",fontWeight:"600"}}>
                Score Scale Reference
            </span>
                <Row
                    gutter={[16, 16]}
                    wrap={false}
                    style={{ marginTop: "10px" }}
                >
                    {[
                        { grade: "F", label: "Critical", range: "0-349", color: "#e977773f", textColor: "#ef4444" },
                        { grade: "D", label: "Poor", range: "350-499", color: "#f974163a", textColor: "#f97316" },
                        { grade: "C", label: "Fair", range: "500-649", color: "#f59f0b3f", textColor: "#f59e0b" },
                        { grade: "B", label: "Good", range: "650-749", color: "#22c55e3c", textColor: "#22c55e" },
                        { grade: "A", label: "Excellent", range: "750-1000", color: "#13c2c233", textColor: "#13c2c2" }
                    ].map((item, index) => (
                        <Col
                            key={index}
                            flex="1 1 20%"
                        >
                            <Card style={{ backgroundColor: item.color, textAlign: "center",height:"100px",alignContent:"center",display:"flex",flexDirection:"column",justifyContent:"center",borderRadius:"12px" }}>
                                <h1>{item.grade}</h1>
                                <span style={{ color: item.textColor, fontWeight: "600" }}>
                                    {item.label}
                                </span>
                                <br />
                                <span style={{ color: "gray" }}>{item.range}</span>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </div>



        </div>
    );
};

export default HumanRiskScoreCard;
